import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, Button, Input } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQuery } from '@tanstack/react-query';
import { Archive, ArchiveRestore, ArrowRightLeft, ChevronLeft, LogOut, Pencil, Plus, Search, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/authContext';
import { useAddMembers, useArchiveProject, useDeleteProject, useProject, useTransferOwnership, useUnarchiveProject, useUpdateProject } from '../../hooks/useProjects';
import { apiErrorMessage } from '../../lib/api/apiErrorMessage';
import { sharingApi } from '../../lib/api/sharingApi';
import { deliveryClient, projectDataClient } from '../../lib/connect/clients';
import { useLayoutStore } from '../../lib/stores/LayoutStore';
import { PageHeader } from '../PageHeader';
import { EditProjectDialog } from './EditProjectDialog';
import { LeaveProjectDialog } from './LeaveProjectDialog';
import { MemberList } from './MemberList';
import { ProjectChatsSection } from './ProjectChatsSection';
import { ProjectFilesSection } from './ProjectFilesSection';
export function ProjectDashboard() {
    const { projectId: id } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { user } = useAuth();
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [leaveProjectDialogOpen, setLeaveProjectDialogOpen] = useState(false);
    const [manageMembersOpen, setManageMembersOpen] = useState(false);
    const [isEditingName, setIsEditingName] = useState(false);
    const [editedName, setEditedName] = useState('');
    const [isEditingDescription, setIsEditingDescription] = useState(false);
    const [editedDescription, setEditedDescription] = useState('');
    // Inline member search state
    const [memberSearchQuery, setMemberSearchQuery] = useState('');
    const [memberSearchResults, setMemberSearchResults] = useState([]);
    const [selectedNewMembers, setSelectedNewMembers] = useState([]);
    const [isSearchingMembers, setIsSearchingMembers] = useState(false);
    const searchTimeoutRef = useRef(null);
    // Inline transfer ownership state
    const [showTransferSection, setShowTransferSection] = useState(false);
    const [transferTargetId, setTransferTargetId] = useState('');
    const [showTransferConfirm, setShowTransferConfirm] = useState(false);
    // Delete confirmation state
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    // Hide breadcrumbs on this page
    const setShowBreadcrumbs = useLayoutStore((state)=>state.setShowBreadcrumbs);
    useEffect(()=>{
        setShowBreadcrumbs(false);
        return ()=>setShowBreadcrumbs(true);
    }, [
        setShowBreadcrumbs
    ]);
    const { data, isLoading, error } = useProject(id ?? null);
    // Contract projects converge on the program workspace: once reverie's sync
    // has materialized the delivery program (the ingest batch's terminus), this
    // page redirects there — the program shell IS the Contract Project
    // workspace. Until then (mid-processing, no program row) the user stays
    // HERE with the batch progress bar; the processing tray links over when the
    // sync lands. ?source=1 is the escape hatch back to raw files/members.
    const staySource = searchParams.get('source') === '1';
    const isContractProject = data?.project.type === 'contract';
    const { data: syncedProgram, isSuccess: programResolved, refetch: refetchProgram } = useQuery({
        queryKey: [
            'program-for-project',
            id
        ],
        enabled: !!id && isContractProject && !!user?.tenant_id,
        staleTime: 30000,
        queryFn: async ()=>{
            // Server-side zou_project_id filter: pangaea resolves it through the
            // programs column AND the converged-project binding table. The old
            // client-side match on p.zouProjectId missed converged projects (their
            // program's column points at the ORIGINAL binder's project).
            const resp = await deliveryClient.listPrograms({
                companyId: user?.tenant_id ?? '',
                search: '',
                sectorId: '',
                businessUnitId: '',
                portfolioId: '',
                operationalStateId: '',
                zouProjectId: id ?? ''
            });
            return resp.programs[0] ?? null;
        }
    });
    // Manual door for contract projects with no backing program (created before
    // programs attached at creation, or award not processed yet): mint the
    // placeholder and go to the program workspace.
    const [creatingProgram, setCreatingProgram] = useState(false);
    const handleCreateDeliveryProgram = async ()=>{
        if (!id || creatingProgram) return;
        setCreatingProgram(true);
        try {
            const resp = await projectDataClient.ensureDeliveryProgram({
                projectId: id
            });
            if (!resp.created && resp.preexisting) {
                navigate(`/delivery/programs/${resp.programId}`);
                return;
            }
            toast.success('Delivery program created');
            await refetchProgram();
            navigate(`/delivery/programs/${resp.programId}`);
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to create the delivery program');
        } finally{
            setCreatingProgram(false);
        }
    };
    const archiveProject = useArchiveProject();
    const unarchiveProject = useUnarchiveProject();
    const deleteProject = useDeleteProject();
    const updateProject = useUpdateProject();
    const addMembers = useAddMembers();
    const transferOwnership = useTransferOwnership();
    // Debounced member search
    useEffect(()=>{
        if (!memberSearchQuery.trim()) {
            setMemberSearchResults([]);
            setIsSearchingMembers(false);
            return;
        }
        setIsSearchingMembers(true);
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        searchTimeoutRef.current = setTimeout(async ()=>{
            try {
                const results = await sharingApi.searchUsers(memberSearchQuery.trim());
                // Filter out existing members and already selected users
                const existingMemberIds = new Set(data?.members.map((m)=>m.userId) ?? []);
                const selectedIds = new Set(selectedNewMembers.map((u)=>u.userId));
                const filtered = results.filter((u)=>!existingMemberIds.has(u.userId) && !selectedIds.has(u.userId));
                setMemberSearchResults(filtered);
            } catch  {
                setMemberSearchResults([]);
            } finally{
                setIsSearchingMembers(false);
            }
        }, 300);
        return ()=>{
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [
        memberSearchQuery,
        data?.members,
        selectedNewMembers
    ]);
    const handleArchive = async ()=>{
        if (!id) return;
        try {
            await archiveProject.mutateAsync(id);
            toast.success('Project archived successfully');
            navigate('/projects');
        } catch (error) {
            // Use error.message verbatim — Connect throws ConnectError and REST
            // The fallback only kicks in if a non-Error value was thrown.
            toast.error(error instanceof Error ? error.message : 'Failed to archive project');
        }
    };
    const handleUnarchive = async ()=>{
        if (!id) return;
        try {
            await unarchiveProject.mutateAsync(id);
            toast.success('Project unarchived successfully');
        } catch (error) {
            toast.error(error instanceof Error ? error.message : 'Failed to unarchive project');
        }
    };
    const handleDelete = async ()=>{
        if (!id) return;
        try {
            await deleteProject.mutateAsync(id);
            toast.success('Project deleted permanently');
            setShowDeleteDialog(false);
            navigate('/projects');
        } catch (error) {
            // useDeleteProject is Connect-only, so this catches ConnectError —
            // its .message carries zou's reason (e.g. "only the project owner
            // can perform this action") which is far more useful than a generic
            // fallback string.
            toast.error(error instanceof Error ? error.message : 'Failed to delete project');
        }
    };
    const handleStartEditingName = ()=>{
        if (!data) return;
        setEditedName(data.project.name);
        setIsEditingName(true);
    };
    const handleSaveName = async ()=>{
        if (!id || !editedName.trim()) {
            setIsEditingName(false);
            return;
        }
        const trimmedName = editedName.trim();
        if (trimmedName === data?.project.name) {
            setIsEditingName(false);
            return;
        }
        try {
            await updateProject.mutateAsync({
                projectId: id,
                name: trimmedName
            });
            setIsEditingName(false);
        } catch (error) {
            toast.error(error instanceof Error ? error.message : 'Failed to update project name');
        }
    };
    const handleNameKeyDown = (e)=>{
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSaveName();
        } else if (e.key === 'Escape') {
            setIsEditingName(false);
        }
    };
    const handleStartEditingDescription = ()=>{
        setEditedDescription(data?.project.description || '');
        setIsEditingDescription(true);
    };
    const handleSaveDescription = async ()=>{
        if (!id) {
            setIsEditingDescription(false);
            return;
        }
        const trimmedDescription = editedDescription.trim();
        if (trimmedDescription === (data?.project.description || '')) {
            setIsEditingDescription(false);
            return;
        }
        try {
            await updateProject.mutateAsync({
                projectId: id,
                description: trimmedDescription
            });
            setIsEditingDescription(false);
        } catch (error) {
            toast.error(error instanceof Error ? error.message : 'Failed to update description');
        }
    };
    const handleDescriptionKeyDown = (e)=>{
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSaveDescription();
        } else if (e.key === 'Escape') {
            setIsEditingDescription(false);
        }
    };
    // Member search handlers
    const handleSelectUser = useCallback((user, role)=>{
        setSelectedNewMembers((prev)=>[
                ...prev,
                {
                    ...user,
                    role
                }
            ]);
        setMemberSearchQuery('');
        setMemberSearchResults([]);
    }, []);
    const handleRemoveSelectedUser = useCallback((userId)=>{
        setSelectedNewMembers((prev)=>prev.filter((u)=>u.userId !== userId));
    }, []);
    const handleAddSelectedMembers = async ()=>{
        if (!id || selectedNewMembers.length === 0) return;
        try {
            await addMembers.mutateAsync({
                projectId: id,
                members: selectedNewMembers.map((u)=>({
                        userId: u.userId,
                        email: u.email,
                        name: u.name,
                        role: u.role
                    }))
            });
            toast.success(`Added ${selectedNewMembers.length} member${selectedNewMembers.length > 1 ? 's' : ''}`);
            setSelectedNewMembers([]);
            setMemberSearchQuery('');
        } catch (err) {
            const message = apiErrorMessage(err, 'Failed to add members');
            toast.error(message);
        }
    };
    const resetMemberSearch = useCallback(()=>{
        setMemberSearchQuery('');
        setMemberSearchResults([]);
        setSelectedNewMembers([]);
    }, []);
    // Transfer ownership handlers
    const handleTransferOwnership = async ()=>{
        if (!id || !transferTargetId) return;
        const targetMember = data?.members.find((m)=>m.userId === transferTargetId);
        try {
            await transferOwnership.mutateAsync({
                projectId: id,
                newOwnerId: transferTargetId
            });
            toast.success(`Ownership transferred to ${targetMember?.name}`);
            setShowTransferSection(false);
            setShowTransferConfirm(false);
            setTransferTargetId('');
        } catch (err) {
            const message = apiErrorMessage(err, 'Failed to transfer ownership');
            toast.error(message);
        }
    };
    const resetTransferOwnership = useCallback(()=>{
        setShowTransferSection(false);
        setShowTransferConfirm(false);
        setTransferTargetId('');
    }, []);
    // Reset drawer state when closing
    const handleCloseDrawer = useCallback(()=>{
        setManageMembersOpen(false);
        resetMemberSearch();
        resetTransferOwnership();
    }, [
        resetMemberSearch,
        resetTransferOwnership
    ]);
    if (isLoading) {
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(PageHeader, {
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "pl-8",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-sm text-muted-foreground mb-1",
                                children: "Loading..."
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "h-6 w-48 bg-gray-200 rounded animate-pulse"
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "flex items-center justify-center h-64",
                    children: /*#__PURE__*/ _jsx("div", {
                        className: "text-gray-500",
                        children: "Loading project..."
                    })
                })
            ]
        });
    }
    if (error || !data || !id) {
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(PageHeader, {
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "pl-8",
                        children: [
                            /*#__PURE__*/ _jsxs(Link, {
                                to: "/projects",
                                className: "text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-1",
                                children: [
                                    /*#__PURE__*/ _jsx(ChevronLeft, {
                                        className: "w-4 h-4"
                                    }),
                                    "Projects"
                                ]
                            }),
                            /*#__PURE__*/ _jsx("h2", {
                                className: "text-xl font-semibold",
                                children: "Project not found"
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "flex items-center justify-center h-64",
                    "data-testid": Testids.project.notFoundState,
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "text-center",
                        children: [
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-red-600 mb-2",
                                children: "This project doesn't exist or you don't have access."
                            }),
                            /*#__PURE__*/ _jsx(Link, {
                                to: "/projects",
                                children: /*#__PURE__*/ _jsx(Button, {
                                    variant: "outline",
                                    children: "Back to Projects"
                                })
                            })
                        ]
                    })
                })
            ]
        });
    }
    const { project, members, role, memberCount } = data;
    // Award-synced contract project → the program workspace is the destination.
    // A PLACEHOLDER program (no contract identity yet — attached at project
    // creation) does not redirect: the overview is where files are uploaded and
    // processed, which is exactly what a pre-award project is for. ?source=1
    // stays here regardless (the escape hatch back to raw files/members).
    if (syncedProgram?.contractNumber && !staySource) {
        return /*#__PURE__*/ _jsx(Navigate, {
            to: `/delivery/programs/${syncedProgram.id}`,
            replace: true
        });
    }
    const projectDetail = {
        ...project,
        members,
        role,
        memberCount
    };
    // id is guaranteed to be defined after the early return check above
    const projectId = id;
    const canEdit = role === 'owner' || role === 'member';
    const isOwner = role === 'owner';
    const isArchived = project.status === 'archived';
    // user may be undefined if auth state is still loading; use empty string to avoid null comparisons
    const currentUserId = user?.id ?? '';
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(PageHeader, {
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "pl-8 pr-6 flex items-start justify-between w-full",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            children: [
                                /*#__PURE__*/ _jsxs(Link, {
                                    to: "/projects",
                                    className: "text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-1",
                                    "data-testid": "back-to-projects-link",
                                    children: [
                                        /*#__PURE__*/ _jsx(ChevronLeft, {
                                            className: "w-4 h-4"
                                        }),
                                        "Projects"
                                    ]
                                }),
                                isEditingName ? /*#__PURE__*/ _jsx(Input, {
                                    value: editedName,
                                    onChange: (e)=>setEditedName(e.target.value),
                                    onBlur: handleSaveName,
                                    onKeyDown: handleNameKeyDown,
                                    className: "text-xl font-display font-semibold h-auto py-0 px-1 -ml-1 max-w-md",
                                    autoFocus: true
                                }) : /*#__PURE__*/ _jsxs("button", {
                                    type: "button",
                                    onClick: canEdit && !isArchived ? handleStartEditingName : undefined,
                                    className: `text-xl font-display font-semibold text-left flex items-center gap-2 group ${canEdit && !isArchived ? 'hover:text-gray-600 cursor-text' : ''}`,
                                    disabled: !canEdit || isArchived,
                                    "aria-label": canEdit && !isArchived ? 'Edit project name' : undefined,
                                    children: [
                                        project.name,
                                        canEdit && !isArchived && /*#__PURE__*/ _jsx(Pencil, {
                                            className: "w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity"
                                        })
                                    ]
                                }),
                                isEditingDescription ? /*#__PURE__*/ _jsx(Input, {
                                    value: editedDescription,
                                    onChange: (e)=>setEditedDescription(e.target.value),
                                    onBlur: handleSaveDescription,
                                    onKeyDown: handleDescriptionKeyDown,
                                    placeholder: "Add a description...",
                                    className: "text-sm text-muted-foreground h-auto py-0 px-1 -ml-1 max-w-xl",
                                    autoFocus: true
                                }) : /*#__PURE__*/ _jsxs("button", {
                                    type: "button",
                                    onClick: canEdit && !isArchived ? handleStartEditingDescription : undefined,
                                    className: `text-sm text-muted-foreground text-left flex items-center gap-2 group max-w-xl ${canEdit && !isArchived ? 'hover:text-gray-500 cursor-text' : ''}`,
                                    disabled: !canEdit || isArchived,
                                    "aria-label": canEdit && !isArchived ? 'Edit project description' : undefined,
                                    children: [
                                        project.description || (canEdit && !isArchived ? 'Add a description...' : ''),
                                        canEdit && !isArchived && project.description && /*#__PURE__*/ _jsx(Pencil, {
                                            className: "w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center gap-3 self-end",
                            children: [
                                /*#__PURE__*/ _jsxs("span", {
                                    className: "text-xs text-muted-foreground",
                                    children: [
                                        "Created",
                                        ' ',
                                        new Date(project.createdAt).toLocaleDateString('en-US', {
                                            month: 'long',
                                            day: 'numeric',
                                            year: 'numeric'
                                        })
                                    ]
                                }),
                                isOwner && !isArchived && /*#__PURE__*/ _jsxs(Button, {
                                    variant: "outline",
                                    size: "sm",
                                    onClick: handleArchive,
                                    disabled: archiveProject.isPending,
                                    className: "text-yellow-600 hover:text-yellow-700 border-yellow-300 hover:border-yellow-400 hover:bg-yellow-50",
                                    "data-testid": "header-archive-button",
                                    children: [
                                        /*#__PURE__*/ _jsx(Archive, {
                                            className: "w-4 h-4 mr-1.5"
                                        }),
                                        "Archive Project"
                                    ]
                                }),
                                isOwner && isArchived && /*#__PURE__*/ _jsxs(Button, {
                                    variant: "outline",
                                    size: "sm",
                                    onClick: handleUnarchive,
                                    disabled: unarchiveProject.isPending,
                                    className: "text-blue-600 hover:text-blue-700 border-blue-300 hover:border-blue-400 hover:bg-blue-50",
                                    "data-testid": "header-unarchive-button",
                                    children: [
                                        /*#__PURE__*/ _jsx(ArchiveRestore, {
                                            className: "w-4 h-4 mr-1.5"
                                        }),
                                        "Restore Project"
                                    ]
                                }),
                                isOwner && /*#__PURE__*/ _jsxs(Button, {
                                    variant: "outline",
                                    size: "sm",
                                    onClick: ()=>setShowDeleteDialog(true),
                                    disabled: deleteProject.isPending,
                                    className: "text-red-600 hover:text-red-700 border-red-300 hover:border-red-400 hover:bg-red-50",
                                    "data-testid": "header-delete-button",
                                    children: [
                                        /*#__PURE__*/ _jsx(Trash2, {
                                            className: "w-4 h-4 mr-1.5"
                                        }),
                                        "Delete Permanently"
                                    ]
                                })
                            ]
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "max-w-7xl pl-14 pr-6 py-6",
                "data-testid": Testids.project.generalDashboard,
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center mb-6",
                        children: [
                            /*#__PURE__*/ _jsxs("button", {
                                type: "button",
                                onClick: ()=>setManageMembersOpen(true),
                                className: "flex items-center hover:opacity-80 transition-opacity group",
                                "data-testid": Testids.project.membersManageTrigger,
                                "aria-label": "Manage project members",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex -space-x-2",
                                        children: [
                                            members.slice(0, 5).map((member, index)=>/*#__PURE__*/ _jsx("div", {
                                                    className: "w-8 h-8 rounded-full bg-gray-200 border-2 border-white flex items-center justify-center text-xs font-medium text-gray-600",
                                                    style: {
                                                        zIndex: 5 - index
                                                    },
                                                    title: member.name,
                                                    children: member.name.charAt(0).toUpperCase()
                                                }, member.userId)),
                                            members.length > 5 && /*#__PURE__*/ _jsxs("div", {
                                                className: "w-8 h-8 rounded-full bg-gray-100 border-2 border-white flex items-center justify-center text-xs font-medium text-gray-500",
                                                style: {
                                                    zIndex: 0
                                                },
                                                children: [
                                                    "+",
                                                    members.length - 5
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "w-8 h-8 rounded-full bg-gray-100 border-2 border-white border-dashed flex items-center justify-center text-gray-400 group-hover:text-gray-600 group-hover:bg-gray-200 transition-colors",
                                                style: {
                                                    zIndex: 0
                                                },
                                                children: /*#__PURE__*/ _jsx(Plus, {
                                                    className: "w-4 h-4"
                                                })
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("span", {
                                        className: "ml-3 text-sm text-gray-500 group-hover:text-gray-700",
                                        children: [
                                            members.length,
                                            " member",
                                            members.length !== 1 ? 's' : ''
                                        ]
                                    })
                                ]
                            }),
                            isContractProject && programResolved && !syncedProgram && canEdit && !isArchived && /*#__PURE__*/ _jsx(Button, {
                                size: "sm",
                                className: "ml-auto",
                                onClick: handleCreateDeliveryProgram,
                                disabled: creatingProgram,
                                "data-testid": Testids.project.createDeliveryProgram,
                                children: creatingProgram ? 'Creating…' : 'Create Delivery Program'
                            }),
                            isContractProject && syncedProgram && !syncedProgram.contractNumber && /*#__PURE__*/ _jsx(Button, {
                                size: "sm",
                                variant: "outline",
                                className: "ml-auto",
                                onClick: ()=>navigate(`/delivery/programs/${syncedProgram.id}`),
                                "data-testid": Testids.project.viewDeliveryProgram,
                                children: "View Delivery Program"
                            })
                        ]
                    }),
                    data.contract && /*#__PURE__*/ _jsxs("div", {
                        className: "mb-6 flex items-center gap-8 rounded-lg border border-gray-200 bg-white px-5 py-4",
                        "data-testid": Testids.project.contractInfoCard,
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "text-xs text-gray-400 uppercase tracking-wider",
                                        children: "Contract Number"
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "text-sm font-semibold text-gray-900",
                                        "data-testid": Testids.project.contractNumberValue,
                                        children: data.contract.contractNumber ?? ''
                                    })
                                ]
                            }),
                            data.contract.contractType && /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "text-xs text-gray-400 uppercase tracking-wider",
                                        children: "Type"
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "text-sm font-semibold text-gray-900",
                                        children: data.contract.contractType
                                    })
                                ]
                            }),
                            data.contract.totalValue && /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "text-xs text-gray-400 uppercase tracking-wider",
                                        children: "Total Value"
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "text-sm font-semibold text-gray-900",
                                        "data-testid": Testids.project.contractValueAmount,
                                        children: new Intl.NumberFormat('en-US', {
                                            style: 'currency',
                                            currency: data.contract.totalValue.currency || 'USD',
                                            maximumFractionDigits: 0
                                        }).format(data.contract.totalValue.totalCeiling)
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex gap-6 h-[calc(100vh-220px)] min-h-[400px]",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "flex-[3] overflow-y-auto pr-2",
                                style: {
                                    scrollbarWidth: 'thin',
                                    scrollbarColor: '#d1d5db transparent'
                                },
                                children: /*#__PURE__*/ _jsx(ProjectFilesSection, {
                                    projectId: projectId,
                                    canEdit: canEdit,
                                    isArchived: isArchived
                                })
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "w-px bg-gray-200 flex-shrink-0"
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "flex-1 overflow-y-auto",
                                children: /*#__PURE__*/ _jsx(ProjectChatsSection, {
                                    projectId: projectId,
                                    userRole: role
                                })
                            })
                        ]
                    }),
                    manageMembersOpen && /*#__PURE__*/ _jsx("div", {
                        className: "fixed inset-0 z-50 bg-black/50",
                        onClick: handleCloseDrawer,
                        children: /*#__PURE__*/ _jsx("div", {
                            className: "absolute right-0 top-0 h-full w-full max-w-lg bg-white shadow-xl overflow-auto",
                            onClick: (e)=>e.stopPropagation(),
                            children: /*#__PURE__*/ _jsxs("div", {
                                className: "p-6",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-center justify-between mb-6",
                                        children: [
                                            /*#__PURE__*/ _jsxs("h2", {
                                                className: "text-lg font-semibold",
                                                children: [
                                                    "Members (",
                                                    memberCount,
                                                    "/25)"
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx(Button, {
                                                variant: "ghost",
                                                size: "sm",
                                                onClick: handleCloseDrawer,
                                                children: "Close"
                                            })
                                        ]
                                    }),
                                    isOwner && !isArchived && memberCount < 25 && /*#__PURE__*/ _jsxs("div", {
                                        className: "mb-4",
                                        children: [
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "relative",
                                                children: [
                                                    /*#__PURE__*/ _jsx(Search, {
                                                        className: "absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                                                    }),
                                                    /*#__PURE__*/ _jsx(Input, {
                                                        type: "text",
                                                        placeholder: "Search users to add...",
                                                        value: memberSearchQuery,
                                                        onChange: (e)=>setMemberSearchQuery(e.target.value),
                                                        className: "pl-9 h-9",
                                                        "data-testid": Testids.project.memberSearchInput
                                                    })
                                                ]
                                            }),
                                            memberSearchQuery.trim() && /*#__PURE__*/ _jsx("div", {
                                                className: "mt-2 border rounded-md bg-white max-h-48 overflow-auto",
                                                children: isSearchingMembers ? /*#__PURE__*/ _jsx("div", {
                                                    className: "p-3 text-sm text-gray-500 text-center",
                                                    children: "Searching..."
                                                }) : memberSearchResults.length === 0 ? /*#__PURE__*/ _jsx("div", {
                                                    className: "p-3 text-sm text-gray-500 text-center",
                                                    children: "No users found"
                                                }) : memberSearchResults.map((user)=>/*#__PURE__*/ _jsxs("div", {
                                                        className: "flex items-center justify-between p-2 hover:bg-gray-50 border-b last:border-b-0",
                                                        "data-testid": Testids.project.memberSearchResultRow,
                                                        children: [
                                                            /*#__PURE__*/ _jsxs("div", {
                                                                className: "flex-1 min-w-0",
                                                                children: [
                                                                    /*#__PURE__*/ _jsx("div", {
                                                                        className: "text-sm font-medium truncate",
                                                                        children: user.name
                                                                    }),
                                                                    /*#__PURE__*/ _jsx("div", {
                                                                        className: "text-xs text-gray-500 truncate",
                                                                        children: user.email
                                                                    })
                                                                ]
                                                            }),
                                                            /*#__PURE__*/ _jsxs("div", {
                                                                className: "flex gap-1 ml-2",
                                                                children: [
                                                                    /*#__PURE__*/ _jsx(Button, {
                                                                        variant: "ghost",
                                                                        size: "sm",
                                                                        onClick: ()=>handleSelectUser(user, 'member'),
                                                                        className: "text-xs h-7 px-2",
                                                                        children: "Member"
                                                                    }),
                                                                    /*#__PURE__*/ _jsx(Button, {
                                                                        variant: "ghost",
                                                                        size: "sm",
                                                                        onClick: ()=>handleSelectUser(user, 'view_only'),
                                                                        className: "text-xs h-7 px-2",
                                                                        children: "View Only"
                                                                    })
                                                                ]
                                                            })
                                                        ]
                                                    }, user.userId))
                                            }),
                                            selectedNewMembers.length > 0 && /*#__PURE__*/ _jsxs("div", {
                                                className: "mt-3 flex flex-wrap items-center gap-2",
                                                children: [
                                                    selectedNewMembers.map((user)=>/*#__PURE__*/ _jsxs("div", {
                                                            className: "inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-full text-sm",
                                                            children: [
                                                                /*#__PURE__*/ _jsx("span", {
                                                                    className: "truncate max-w-[120px]",
                                                                    children: user.name
                                                                }),
                                                                /*#__PURE__*/ _jsxs("span", {
                                                                    className: "text-xs text-gray-500",
                                                                    children: [
                                                                        "(",
                                                                        user.role.replace('_', ' '),
                                                                        ")"
                                                                    ]
                                                                }),
                                                                /*#__PURE__*/ _jsx("button", {
                                                                    type: "button",
                                                                    onClick: ()=>handleRemoveSelectedUser(user.userId),
                                                                    className: "ml-1 text-gray-400 hover:text-gray-600",
                                                                    "aria-label": `Remove ${user.name}`,
                                                                    children: /*#__PURE__*/ _jsx(X, {
                                                                        className: "w-3 h-3"
                                                                    })
                                                                })
                                                            ]
                                                        }, user.userId)),
                                                    /*#__PURE__*/ _jsx(Button, {
                                                        size: "sm",
                                                        onClick: handleAddSelectedMembers,
                                                        disabled: addMembers.isPending,
                                                        className: "h-7",
                                                        "data-testid": Testids.project.addSelectedMembersButton,
                                                        children: addMembers.isPending ? 'Adding...' : `Add ${selectedNewMembers.length}`
                                                    })
                                                ]
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(MemberList, {
                                        project: projectDetail,
                                        currentUserId: currentUserId
                                    }),
                                    !isArchived && /*#__PURE__*/ _jsxs("div", {
                                        className: "mt-6 pt-4 border-t",
                                        children: [
                                            isOwner && /*#__PURE__*/ _jsx("div", {
                                                className: "mb-4",
                                                children: !showTransferSection ? /*#__PURE__*/ _jsxs(Button, {
                                                    variant: "ghost",
                                                    size: "sm",
                                                    onClick: ()=>setShowTransferSection(true),
                                                    className: "text-gray-500",
                                                    "data-testid": "transfer-ownership-button",
                                                    children: [
                                                        /*#__PURE__*/ _jsx(ArrowRightLeft, {
                                                            className: "w-4 h-4 mr-1"
                                                        }),
                                                        "Transfer Ownership"
                                                    ]
                                                }) : /*#__PURE__*/ _jsxs("div", {
                                                    className: "p-3 border rounded-md bg-gray-50",
                                                    children: [
                                                        /*#__PURE__*/ _jsxs("div", {
                                                            className: "flex items-center justify-between mb-3",
                                                            children: [
                                                                /*#__PURE__*/ _jsx("span", {
                                                                    className: "text-sm font-medium",
                                                                    children: "Transfer Ownership"
                                                                }),
                                                                /*#__PURE__*/ _jsx("button", {
                                                                    type: "button",
                                                                    onClick: resetTransferOwnership,
                                                                    className: "text-gray-400 hover:text-gray-600",
                                                                    "aria-label": "Cancel transfer",
                                                                    children: /*#__PURE__*/ _jsx(X, {
                                                                        className: "w-4 h-4"
                                                                    })
                                                                })
                                                            ]
                                                        }),
                                                        !showTransferConfirm ? /*#__PURE__*/ _jsxs(_Fragment, {
                                                            children: [
                                                                /*#__PURE__*/ _jsxs("select", {
                                                                    value: transferTargetId,
                                                                    onChange: (e)=>setTransferTargetId(e.target.value),
                                                                    className: "w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 mb-3",
                                                                    "data-testid": "transfer-owner-select",
                                                                    children: [
                                                                        /*#__PURE__*/ _jsx("option", {
                                                                            value: "",
                                                                            children: "Select new owner..."
                                                                        }),
                                                                        members.filter((m)=>m.userId !== currentUserId && m.role !== 'owner').map((member)=>/*#__PURE__*/ _jsxs("option", {
                                                                                value: member.userId,
                                                                                children: [
                                                                                    member.name,
                                                                                    " (",
                                                                                    member.email,
                                                                                    ")"
                                                                                ]
                                                                            }, member.userId))
                                                                    ]
                                                                }),
                                                                /*#__PURE__*/ _jsx(Button, {
                                                                    size: "sm",
                                                                    variant: "outline",
                                                                    onClick: ()=>setShowTransferConfirm(true),
                                                                    disabled: !transferTargetId,
                                                                    className: "w-full",
                                                                    children: "Continue"
                                                                })
                                                            ]
                                                        }) : /*#__PURE__*/ _jsxs(_Fragment, {
                                                            children: [
                                                                /*#__PURE__*/ _jsxs("div", {
                                                                    className: "p-3 bg-yellow-50 border border-yellow-200 rounded-md mb-3",
                                                                    children: [
                                                                        /*#__PURE__*/ _jsx("p", {
                                                                            className: "text-sm font-medium text-yellow-900 mb-2",
                                                                            children: "Warning: This cannot be undone"
                                                                        }),
                                                                        /*#__PURE__*/ _jsxs("ul", {
                                                                            className: "text-xs text-yellow-800 space-y-1 list-disc list-inside",
                                                                            children: [
                                                                                /*#__PURE__*/ _jsxs("li", {
                                                                                    children: [
                                                                                        /*#__PURE__*/ _jsx("strong", {
                                                                                            children: members.find((m)=>m.userId === transferTargetId)?.name
                                                                                        }),
                                                                                        ' ',
                                                                                        "will become the new owner"
                                                                                    ]
                                                                                }),
                                                                                /*#__PURE__*/ _jsx("li", {
                                                                                    children: "You will be demoted to Member role"
                                                                                }),
                                                                                /*#__PURE__*/ _jsx("li", {
                                                                                    children: "The new owner will have full control"
                                                                                })
                                                                            ]
                                                                        })
                                                                    ]
                                                                }),
                                                                /*#__PURE__*/ _jsxs("div", {
                                                                    className: "flex gap-2",
                                                                    children: [
                                                                        /*#__PURE__*/ _jsx(Button, {
                                                                            size: "sm",
                                                                            variant: "outline",
                                                                            onClick: ()=>setShowTransferConfirm(false),
                                                                            disabled: transferOwnership.isPending,
                                                                            className: "flex-1",
                                                                            children: "Back"
                                                                        }),
                                                                        /*#__PURE__*/ _jsx(Button, {
                                                                            size: "sm",
                                                                            variant: "destructive",
                                                                            onClick: handleTransferOwnership,
                                                                            disabled: transferOwnership.isPending,
                                                                            className: "flex-1",
                                                                            "data-testid": "confirm-transfer-button",
                                                                            children: transferOwnership.isPending ? 'Transferring...' : 'Transfer'
                                                                        })
                                                                    ]
                                                                })
                                                            ]
                                                        })
                                                    ]
                                                })
                                            }),
                                            !isOwner && /*#__PURE__*/ _jsxs(Button, {
                                                variant: "ghost",
                                                size: "sm",
                                                onClick: ()=>{
                                                    handleCloseDrawer();
                                                    setLeaveProjectDialogOpen(true);
                                                },
                                                className: "text-red-500 hover:text-red-600 hover:bg-red-50",
                                                "data-testid": "leave-project-button",
                                                children: [
                                                    /*#__PURE__*/ _jsx(LogOut, {
                                                        className: "w-4 h-4 mr-1"
                                                    }),
                                                    "Leave Project"
                                                ]
                                            })
                                        ]
                                    })
                                ]
                            })
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(EditProjectDialog, {
                open: editDialogOpen,
                onOpenChange: setEditDialogOpen,
                project: projectDetail
            }),
            /*#__PURE__*/ _jsx(LeaveProjectDialog, {
                open: leaveProjectDialogOpen,
                onOpenChange: setLeaveProjectDialogOpen,
                project: projectDetail,
                currentUserId: currentUserId,
                onTransferOwnership: ()=>{
                    setLeaveProjectDialogOpen(false);
                    setManageMembersOpen(true);
                    setShowTransferSection(true);
                },
                onArchiveProject: ()=>{
                    setLeaveProjectDialogOpen(false);
                    handleArchive();
                }
            }),
            /*#__PURE__*/ _jsx(AlertDialog, {
                open: showDeleteDialog,
                onOpenChange: setShowDeleteDialog,
                children: /*#__PURE__*/ _jsxs(AlertDialogContent, {
                    children: [
                        /*#__PURE__*/ _jsxs(AlertDialogHeader, {
                            children: [
                                /*#__PURE__*/ _jsx(AlertDialogTitle, {
                                    children: "Delete Project Permanently?"
                                }),
                                /*#__PURE__*/ _jsxs(AlertDialogDescription, {
                                    children: [
                                        "This will ",
                                        /*#__PURE__*/ _jsx("strong", {
                                            children: "permanently delete"
                                        }),
                                        " ",
                                        /*#__PURE__*/ _jsx("strong", {
                                            children: project.name
                                        }),
                                        " and all associated data including files, chats, and team members. This action cannot be undone."
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(AlertDialogFooter, {
                            children: [
                                /*#__PURE__*/ _jsx(AlertDialogCancel, {
                                    children: "Cancel"
                                }),
                                /*#__PURE__*/ _jsx(AlertDialogAction, {
                                    onClick: handleDelete,
                                    className: "bg-red-600 hover:bg-red-700",
                                    children: "Delete Permanently"
                                })
                            ]
                        })
                    ]
                })
            })
        ]
    });
}
